/**
 * WorkspaceSwitcher — active context indicator and workspace selector.
 *
 * Until now the active workspace was an invisible bootstrap default: it only
 * existed as `active_workspace_id` in localStorage (which `lib/api.ts` turns
 * into the `X-Workspace-Id` header). This component makes it visible in the
 * shell header as `Organization › Workspace` and lets the operator switch it.
 *
 * On switch:
 * 1. Updates localStorage (`active_workspace_id`)
 * 2. Re-bootstraps the session, so the backend resolves and echoes back the
 *    workspace it actually accepted for the active organization
 * 3. Invalidates cached queries so mounted screens refetch under the new
 *    workspace header — no full page reload
 *
 * Workspaces come from the session bootstrap payload (`session.workspaces`),
 * which the backend already scopes to the active organization; switching the
 * client via OrgSwitcher clears `active_workspace_id` so bootstrap resolves
 * that organization's default workspace.
 */

import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Check, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSession } from "@/features/auth/useSession";
import { useI18n } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { BootstrapWorkspace } from "@/lib/bootstrap-types";

/** Separator between the organization and the workspace in the indicator. */
const CONTEXT_SEPARATOR = "›";

const TRIGGER_CLASSES = cn(
  "flex max-w-[240px] items-center gap-1.5 rounded-md px-2 py-1",
  "text-[12.5px] text-[var(--fg-muted)] transition-colors",
  "hover:bg-[var(--bg-muted)] hover:text-[var(--fg)]",
  "disabled:cursor-default disabled:opacity-70 disabled:hover:bg-transparent",
);

export function WorkspaceSwitcher() {
  const { session, refreshSession } = useSession();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const workspaces = session?.workspaces ?? [];
  const activeWorkspaceId = session?.active_workspace?.id ?? null;

  // Hooks must stay above every early return so the hook order is stable
  // across renders (same constraint documented in OrgSwitcher for #388).
  const handleSelect = useCallback(
    async (workspace: BootstrapWorkspace) => {
      if (workspace.id === activeWorkspaceId) {
        setOpen(false);
        return;
      }

      setSwitching(true);
      await queryClient.cancelQueries();

      // The axios interceptor reads this key on every request, so persisting
      // it here is also what makes the choice survive a reload.
      localStorage.setItem("active_workspace_id", workspace.id);

      try {
        await refreshSession();
        await queryClient.invalidateQueries({ refetchType: "active" });
      } finally {
        setSwitching(false);
        setOpen(false);
      }
    },
    [activeWorkspaceId, queryClient, refreshSession],
  );

  if (!session) return null;

  const organizationLabel =
    session.active_organization?.name ?? t("common.selectClient");
  const workspaceLabel =
    session.active_workspace?.name ?? t("common.noWorkspace");
  const contextLabel = `${organizationLabel} ${CONTEXT_SEPARATOR} ${workspaceLabel}`;
  const ariaLabel = t("common.activeContext", {
    organization: organizationLabel,
    workspace: workspaceLabel,
  });

  // No workspaces for this organization: keep the context visible and inert
  // instead of offering an empty menu.
  if (workspaces.length === 0) {
    return (
      <button
        type="button"
        disabled
        aria-label={ariaLabel}
        className={TRIGGER_CLASSES}
      >
        <Layers className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{contextLabel}</span>
      </button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            TRIGGER_CLASSES,
            switching && "pointer-events-none opacity-50",
          )}
        >
          <Layers className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{contextLabel}</span>
          <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-muted-foreground text-xs">
          {t("common.workspace")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.map((workspace) => (
          <DropdownMenuItem
            key={workspace.id}
            role="menuitemradio"
            aria-checked={workspace.id === activeWorkspaceId}
            onClick={() => void handleSelect(workspace)}
            disabled={switching}
            className="cursor-pointer"
          >
            <div className="flex w-full items-center gap-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{workspace.name}</p>
                <p className="text-muted-foreground truncate font-mono text-xs">
                  {workspace.slug}
                </p>
              </div>
              {workspace.id === activeWorkspaceId && (
                <Check className="h-4 w-4 shrink-0 text-green-500" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
